import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, BookOpen, CalendarDays, Check, ChevronRight, CircleCheck, Cloud, Code2, Coffee, Database, Flag, Flame,
  GraduationCap, Layers, Newspaper, NotebookPen, PenLine, Play, PlayCircle, Plus, ShieldCheck, Target, Trophy,
} from 'lucide-react';
import { useCourse } from '../contexts/CourseContext';
import { useAuth } from '../contexts/AuthContext';
import { articleService, courseService, dashboardService, tagService, trailService } from '../services/api';
import { TRAIL_TYPES, fmtHours, fmtHoursRound } from '../utils/trails';
import { useDive } from '../components/DiveTransition';
import { getCourseColor, getPhaseColor } from '../theme/phases';
import { Brand, Button, EmptyState, Loader, UserMenu, DemoBar } from '../components/ui';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { MadeBy } from '../components/MadeBy';
import './CourseSelector.css';

const COURSE_ICONS = {
  school: GraduationCap,
  cloud: Cloud,
  code: Code2,
  security: ShieldCheck,
  data: Database,
};

const QUOTES = [
  ['O conhecimento é a única coisa que ninguém pode tirar de você.', 'B.B. King'],
  ['A educação é a arma mais poderosa que você pode usar para mudar o mundo.', 'Nelson Mandela'],
  ['Tudo parece impossível até que seja feito.', 'Nelson Mandela'],
  ['Ninguém ignora tudo. Ninguém sabe tudo. Todos nós sabemos alguma coisa. Todos nós ignoramos alguma coisa.', 'Paulo Freire'],
  ['O sucesso é a soma de pequenos esforços repetidos dia após dia.', 'Robert Collier'],
  ['Quanto mais eu pratico, mais sorte eu tenho.', 'Gary Player'],
  ['Não é que eu seja tão inteligente; é que fico mais tempo com os problemas.', 'Albert Einstein'],
  ['A raiz da educação é amarga, mas o fruto é doce.', 'Aristóteles'],
];

/** Tela inicial: hoje primeiro (plano da trilha e o que está pendente nos cursos), depois cursos, trilhas e artigos. */
function CourseSelector() {
  const navigate = useNavigate();
  const dive = useDive();
  const { user } = useAuth();
  const { courses, loading, error, selectCourse, loadCourses } = useCourse();
  const [overview, setOverview] = useState({});
  const [dashboards, setDashboards] = useState({});
  const [articles, setArticles] = useState(null);
  const [followed, setFollowed] = useState(null);
  const [popularTags, setPopularTags] = useState([]);
  const [today, setToday] = useState(null);
  const [trails, setTrails] = useState(null);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    // Progresso por fase e a última aula de cada curso; depois o painel de cada um (nível, revisões, sequência)
    courseService.overview()
      .then(list => {
        setOverview(Object.fromEntries(list.map(c => [c.slug, c])));
        return Promise.all(list.map(c => dashboardService.getDashboard(c.slug).then(d => [c.slug, d]).catch(() => [c.slug, null])));
      })
      .then(pairs => setDashboards(Object.fromEntries(pairs)))
      .catch(() => {});
    articleService.highlights(undefined, 3).then(setArticles).catch(() => setArticles([]));
    tagService.following().then(setFollowed).catch(() => setFollowed([]));
    articleService.sidebar().then(s => setPopularTags((s.popularTags || []).slice(0, 5))).catch(() => {});
    trailService.today().then(setToday).catch(() => setToday(null));
    trailService.catalog().then(d => setTrails(d.trails)).catch(() => setTrails([]));
  }, []);

  // A animação de "mergulho" roda antes de entrar no curso, na trilha ou no artigo
  const enterCourse = (course, path = '') => dive('curso', async () => {
    await selectCourse(course.slug);
    navigate(`/curso/${course.slug}${path}`);
  });
  const goArticles = () => dive('artigos', () => navigate('/artigos'));
  const goTrails = () => dive('trilhas', () => navigate('/trilhas'));
  const goCourses = () => dive('cursos', () => navigate('/cursos'));
  const openTrail = (t, path = '') => dive('trilha', () => navigate(`/trilhas/${t.slug}${path}`));
  const openArticle = (a) => dive('artigo', () => navigate(`/artigos/${a.slug}`));

  // Conta "Só artigos": sem cursos liberados
  const articlesOnly = user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN' && !user.allCourses && user.courseIds?.length === 0;

  const day = useMemo(() => buildDay({ today, courses, overview, dashboards }), [today, courses, overview, dashboards]);
  const streak = Object.values(dashboards).find(d => d?.activity)?.activity;

  return (
    <div className="selector">
      <DemoBar />
      <div className="selector__strip" aria-hidden="true" />

      <div className="selector__inner">
        <div className="selector__top">
          <Brand size="md" onDark />
          <div className="selector__topright">
            <NotificationBell />
            <UserMenu />
          </div>
        </div>

        <header className="inicio-hero">
          <div className="inicio-hero__main">
            <Quotes />
            <span className="inicio-eyebrow">{dateLabel()}</span>
            <h1 className="inicio-hero__title">{greeting()}{firstName(user) && <>, <span>{firstName(user)}.</span></>}</h1>
            <p className="inicio-hero__sub">{articlesOnly ? 'Os artigos da turma estão logo abaixo.' : day.subtitle}</p>
          </div>
          {streak && (streak.streak > 0 || streak.longestStreak > 0) && (
            <div className="inicio-streak">
              <Flame size={20} aria-hidden="true" />
              <span>
                <strong>{streak.streak > 0 ? `${streak.streak} ${streak.streak === 1 ? 'dia' : 'dias'}` : 'Comece hoje'}</strong>
                <small>{streak.streak > 0 ? 'de sequência' : 'sua sequência'} · recorde {streak.longestStreak}</small>
              </span>
            </div>
          )}
        </header>

        {!articlesOnly && (day.top || day.items.length > 0) && (
          <TodayBlock
            day={day}
            onTop={() => {
              const t = day.top;
              if (t.trail) openTrail({ slug: t.trail }, t.path);
              else if (t.course) enterCourse(t.course, t.path);
            }}
            onItem={(it) => enterCourse(it.course, it.path)}
            onRead={goArticles}
          />
        )}

        <section className="inicio-section" data-tour="cursos">
          <SectionHead eyebrow="Para estudar" title="Cursos"
            link={!loading && !error && courses.length > 0 ? { label: `Ver todos os cursos · ${courses.length}`, onClick: goCourses } : null} />

          {loading && <Loader label="Carregando cursos..." />}

          {!loading && error && (
            <div className="selector__state">
              <EmptyState
                file="erro.xml"
                title="Não foi possível carregar os cursos"
                description={error}
                action={<Button onClick={loadCourses}>Tentar novamente</Button>}
              />
            </div>
          )}

          {!loading && !error && courses.length === 0 && (
            <div className="selector__state">
              <EmptyState
                file="cursos.xml"
                title={articlesOnly ? 'Sua conta é só de artigos' : 'Nenhum curso disponível'}
                description={articlesOnly
                  ? 'Você lê, curte e comenta os artigos da turma. Para estudar um curso, peça ao administrador.'
                  : 'Não há cursos cadastrados no momento.'}
                action={articlesOnly && <Button icon={Newspaper} onClick={goArticles}>Ver artigos</Button>}
              />
            </div>
          )}

          {!loading && !error && courses.length > 0 && (
            <div className="inicio-grid">
              {courses.map((course, index) => (
                <HomeCourse
                  key={course.id}
                  course={course}
                  color={getCourseColor(index)}
                  ov={overview[course.slug]}
                  level={dashboards[course.slug]?.gamification?.level}
                  onOpen={() => enterCourse(course)}
                  onContinue={(path) => enterCourse(course, path)}
                />
              ))}
            </div>
          )}
        </section>

        {trails?.length > 0 && (
          <HomeTrails trails={trails} today={today} onAll={goTrails} onOpen={openTrail} />
        )}

        <section className="inicio-section">
          <SectionHead eyebrow="Para ler" title="Artigos" link={{ label: 'Ver todos os artigos', onClick: goArticles }} />
          {followed?.length === 0 && popularTags.length > 0 && <FollowTags tags={popularTags} />}
          {articles && articles.length === 0 ? (
            <div className="selector__noarticles">
              <span>Ainda não há artigos publicados. Que tal escrever o primeiro?</span>
              {!user?.demo && <Button variant="outline" icon={PenLine} onClick={() => navigate('/artigos/novo')}>Escrever artigo</Button>}
            </div>
          ) : (
            <div className="inicio-grid">
              {(articles || []).map(a => <HomeArticle key={a.id} article={a} onOpen={() => openArticle(a)} />)}
            </div>
          )}
        </section>

        <footer className="inicio-foot">
          <span>StudySpace · estudo sem limite</span>
          <MadeBy onDark className="inicio-foot__madeby" />
        </footer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// "Para hoje"
// ═══════════════════════════════════════════════════════════════

/**
 * Monta o bloco "Para hoje": o topo (plano da trilha, folga, plano feito ou a última aula) e as revisões
 * pendentes nos cursos (flashcards, simulado em andamento, caderno de erros, sequência em risco).
 */
function buildDay({ today, courses, overview, dashboards }) {
  const bySlug = Object.fromEntries(courses.map(c => [c.slug, c]));
  const dash = Object.entries(dashboards).filter(([slug, d]) => d && bySlug[slug]);
  const items = [];

  const flash = dash.filter(([, d]) => d.dueFlashcards > 0).sort((a, b) => b[1].dueFlashcards - a[1].dueFlashcards);
  if (flash.length) {
    const total = flash.reduce((n, [, d]) => n + d.dueFlashcards, 0);
    const [slug] = flash[0];
    items.push({
      key: 'flash', icon: Layers, tone: 'navy', course: bySlug[slug], path: '/flashcards', action: 'Revisar',
      title: `${total} ${total === 1 ? 'flashcard' : 'flashcards'}`,
      meta: `${bySlug[slug].name} · ~${Math.max(1, Math.round(total / 3))} min`,
    });
  }
  const exam = dash.find(([, d]) => d.pendingExam);
  if (exam) {
    const [slug, d] = exam;
    const p = d.pendingExam;
    items.push({
      key: 'exam', icon: Target, tone: 'amber', course: bySlug[slug], path: `/exam/${p.id}`, action: 'Retomar',
      title: p.idleDays >= 1 ? `Simulado parado há ${p.idleDays} ${p.idleDays === 1 ? 'dia' : 'dias'}` : 'Simulado em andamento',
      meta: `${bySlug[slug].name} · ${p.answered} de ${p.totalQuestions} respondidas`,
    });
  }
  const errs = dash.filter(([, d]) => d.totalErrors > 0).sort((a, b) => b[1].totalErrors - a[1].totalErrors);
  if (errs.length) {
    const total = errs.reduce((n, [, d]) => n + d.totalErrors, 0);
    const [slug] = errs[0];
    items.push({
      key: 'errors', icon: NotebookPen, tone: 'danger', course: bySlug[slug], path: '/erros', action: 'Revisar',
      title: `${total} ${total === 1 ? 'erro' : 'erros'} no caderno`,
      meta: `Caderno de erros · ${bySlug[slug].name}`,
    });
  }

  // Última aula vista, em qualquer curso: é o "continue de onde parou" sem trilha
  const last = Object.values(overview)
    .filter(o => o.lastLesson && bySlug[o.slug])
    .sort((a, b) => new Date(b.lastLesson.viewedAt) - new Date(a.lastLesson.viewedAt))[0];
  const lessonPath = (l) => `/estudo/${l.topicId}/aula/${l.lessonId}`;

  const activity = dash.find(([, d]) => d.activity)?.[1].activity;
  if (items.length < 3 && activity?.streak > 0 && !activity.studiedToday) {
    const target = last ? bySlug[last.slug] : courses[0];
    if (target) {
      items.push({
        key: 'streak', icon: Flame, tone: 'amber', course: target, path: last ? lessonPath(last.lastLesson) : '/estudo', action: 'Estudar',
        title: `Sequência de ${activity.streak} ${activity.streak === 1 ? 'dia' : 'dias'}`,
        meta: 'Uma aula ou alguns flashcards hoje já mantêm',
      });
    }
  }
  const reviews = items.slice(0, 3);
  const nReviews = reviews.length;
  const revisions = nReviews === 1 ? '1 revisão' : `${nReviews} revisões`;

  let top = null;
  let subtitle;
  if (today && today.studyDay) {
    const stage = today.stageNumber ? ` · Etapa ${today.stageNumber}` : '';
    const eyebrow = `Plano de hoje · Dia ${today.day} de ${today.totalDays}${today.minutes ? ` · ${fmtHours(today.minutes)}` : ''}`;
    const cp = today.checkpoint;
    const nextCp = cp && !cp.late ? ` · próximo checkpoint ${inDaysLabel(cp.inDays)}` : '';
    if (today.doneToday) {
      top = {
        kind: 'done', eyebrow, chip: { label: 'Concluído', tone: 'lime', icon: Check },
        title: `Você fez as ${fmtHours(today.minutes)} de hoje.`, meta: `${today.trailTitle}${nextCp}`,
        cta: 'Adiantar amanhã', ghost: true, trail: today.slug, path: '/plano',
      };
      subtitle = 'Plano de hoje feito. Bom trabalho.';
    } else {
      top = {
        kind: 'play', eyebrow,
        chip: today.late > 0
          ? { label: `${today.late} ${today.late === 1 ? 'checkpoint atrasado' : 'checkpoints atrasados'}`, tone: 'danger', icon: Flag }
          : { label: 'Em dia', tone: 'lime', icon: Check },
        title: today.title, meta: `${today.trailTitle}${stage}`,
        cta: 'Começar o dia', trail: today.slug, path: today.stageNumber ? `/etapas/${today.stageNumber}` : '/plano',
      };
      const count = nReviews + 1;
      subtitle = today.late > 0
        ? `Você tem ${count} ${count === 1 ? 'coisa' : 'coisas'} para hoje. Comece pelo checkpoint atrasado.`
        : `Tudo em dia. Hoje são ${fmtHours(today.minutes)} de plano${nReviews ? ` e ${revisions}` : ''}.`;
    }
  } else if (today) {
    top = {
      kind: 'rest', eyebrow: 'Hoje não é dia de estudo no plano', title: today.title, meta: today.subtitle,
      cta: 'Estudar mesmo assim', ghost: true, trail: today.slug, path: '/plano',
    };
    subtitle = nReviews ? 'Dia livre no plano. Se sobrar tempo, as revisões estão aqui.' : 'Dia livre no plano.';
  } else if (last) {
    const c = bySlug[last.slug];
    top = {
      kind: 'play', eyebrow: `Continue de onde parou · ${c.name}`, title: last.lastLesson.name, meta: last.lastLesson.topicName,
      cta: 'Continuar aula', course: c, path: lessonPath(last.lastLesson),
    };
    subtitle = 'Retome o curso ou escolha uma trilha para ter um plano diário.';
  } else if (courses.length) {
    top = {
      kind: 'play', eyebrow: 'Comece por aqui', title: courses[0].name, meta: 'Primeira aula do curso',
      cta: 'Começar', course: courses[0], path: '/estudo',
    };
    subtitle = 'Escolha um curso para começar ou uma trilha para ter um plano diário.';
  } else {
    subtitle = 'Escolha um curso, uma trilha ou um artigo para começar.';
  }
  return { top, items: reviews, subtitle };
}

function TodayBlock({ day, onTop, onItem, onRead }) {
  const { top, items } = day;
  const TopIcon = top?.kind === 'done' ? Check : top?.kind === 'rest' ? Coffee : Play;
  return (
    <section className="inicio-today" aria-label="Para hoje" data-tour="hoje">
      {top && (
        <div className="inicio-today__top">
          <span className={`inicio-today__icon is-${top.kind}`}><TopIcon size={24} aria-hidden="true" /></span>
          <div className="inicio-today__text">
            <span className="inicio-today__eyebrow">
              {top.eyebrow}
              {top.chip && (
                <span className={`inicio-chip inicio-chip--${top.chip.tone}`}>
                  <top.chip.icon size={11} strokeWidth={2.6} aria-hidden="true" />{top.chip.label}
                </span>
              )}
            </span>
            <strong>{top.title}</strong>
            {top.meta && <span className="inicio-today__meta">{top.meta}</span>}
          </div>
          <button type="button" className={`inicio-today__cta ${top.ghost ? 'is-ghost' : ''}`} onClick={onTop}>
            {top.cta} <ArrowRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="inicio-today__items">
        {items.length === 0 ? (
          <button type="button" className="inicio-today__item inicio-today__item--empty" onClick={onRead}>
            <span className="inicio-ico inicio-ico--lime"><CircleCheck size={17} aria-hidden="true" /></span>
            <span className="inicio-today__itext">
              <strong>Nada pendente nos cursos</strong>
              <span>Flashcards revisados, nenhum simulado parado, caderno de erros em dia.</span>
            </span>
            <span className="inicio-today__action">Ler um artigo <ChevronRight size={15} aria-hidden="true" /></span>
          </button>
        ) : items.map(it => (
          <button key={it.key} type="button" className="inicio-today__item" onClick={() => onItem(it)}>
            <span className={`inicio-ico inicio-ico--${it.tone}`}><it.icon size={17} aria-hidden="true" /></span>
            <span className="inicio-today__itext">
              <strong>{it.title}</strong>
              <span>{it.meta}</span>
            </span>
            <span className="inicio-today__action">{it.action} <ChevronRight size={15} aria-hidden="true" /></span>
          </button>
        ))}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// Cursos, trilhas e artigos
// ═══════════════════════════════════════════════════════════════

function SectionHead({ eyebrow, title, link }) {
  return (
    <div className="inicio-section__head">
      <div>
        <span className="inicio-eyebrow">{eyebrow}</span>
        <h2 className="inicio-section__title">{title}</h2>
      </div>
      {link && (
        <button type="button" className="inicio-link" onClick={link.onClick}>
          {link.label} <ArrowRight size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Card de curso: nível, progresso por fase e o "Continuar" na última aula vista. */
function HomeCourse({ course, color, ov, level, onOpen, onContinue }) {
  const Icon = COURSE_ICONS[course.icon] || BookOpen;
  const phases = ov?.phases || [];
  const lessons = ov?.lessons ?? 0;
  const done = ov?.done ?? 0;
  const last = ov?.lastLesson;
  // "Pós-Tech FIAP - Especialização em ..." → "Pós-Tech FIAP"; descrição longa sem esse formato fica para /cursos
  const head = course.description ? course.description.split(/\s[-–]\s/)[0] : null;
  const origin = head && head.length <= 32 ? head : null;
  const meta = [origin, `${course.phasesCount || phases.length || 0} fases`, lessons ? `${lessons} aulas` : null].filter(Boolean).join(' · ');
  const go = (e) => {
    e.stopPropagation();
    onContinue(last ? `/estudo/${last.topicId}/aula/${last.lessonId}` : '/estudo');
  };
  return (
    <div
      className="inicio-card inicio-course"
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
    >
      <div className="inicio-course__head">
        <span className="inicio-course__icon" style={{ background: color }}><Icon size={21} aria-hidden="true" /></span>
        {level && <span className="inicio-pill inicio-pill--lime"><Trophy size={11} aria-hidden="true" />Nível {level}</span>}
      </div>
      <h3 className="inicio-course__name">{course.name}</h3>
      {meta && <span className="inicio-course__meta">{meta}</span>}
      <div className="inicio-course__progress">
        <div className="inicio-course__nums">
          <strong>{ov?.pct ?? 0}%</strong>
          <span>{done} de {lessons} aulas</span>
        </div>
        {phases.length > 0 && (
          <div className="inicio-segbar" aria-hidden="true">
            {phases.map(p => (
              <span key={p.number} className="inicio-segbar__seg">
                <span style={{ width: `${p.lessons ? Math.round(p.done * 100 / p.lessons) : 0}%`, background: getPhaseColor(p.number) }} />
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="inicio-course__foot">
        <span className="inicio-course__last">
          <small>{last ? 'Você parou em' : 'Primeira aula'}</small>
          <span>{last ? `${last.topicName} · ${last.name}` : phases[0]?.name || 'Fase 1'}</span>
        </span>
        <button type="button" className="inicio-btn" onClick={go}>
          <Play size={13} aria-hidden="true" />{last ? 'Continuar' : 'Começar'}
        </button>
      </div>
    </div>
  );
}

/** Trilhas: a ativa em destaque (com o aviso do dia) e as outras compactas ao lado. */
function HomeTrails({ trails, today, onAll, onOpen }) {
  const active = trails.find(t => t.enrollment?.active);
  const others = trails.filter(t => t !== active).slice(0, active ? 2 : 3);
  return (
    <section className="inicio-section" data-tour="trilhas-home">
      <SectionHead eyebrow="Para chegar lá" title="Trilhas" link={{ label: `Ver todas as trilhas · ${trails.length}`, onClick: onAll }} />
      <div className={active ? 'inicio-trails' : 'inicio-grid'}>
        {active && <ActiveTrail trail={active} today={today?.slug === active.slug ? today : null} onOpen={onOpen} />}
        {others.length > 0 && (
          <div className={active ? 'inicio-trails__side' : 'inicio-trails__row'}>
            {others.map(t => <CompactTrail key={t.id} trail={t} onOpen={() => onOpen(t)} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function TypeBadge({ type }) {
  const tp = TRAIL_TYPES[type] || TRAIL_TYPES.certification;
  return <span className="inicio-pill inicio-pill--lime"><tp.icon size={11} aria-hidden="true" />{tp.label}</span>;
}

function ActiveTrail({ trail: t, today, onOpen }) {
  const en = t.enrollment;
  const alert = trailAlert(t, today, en);
  return (
    <div className="inicio-card inicio-trail" role="link" tabIndex={0} onClick={() => onOpen(t)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t); }}>
      <div className="inicio-trail__body">
        <div className="inicio-trail__badges">
          <TypeBadge type={t.type} />
          <span className="inicio-pill inicio-pill--navy"><PlayCircle size={11} aria-hidden="true" />Em andamento</span>
          {t.examCode && <code className="inicio-trail__code">{t.examCode}</code>}
        </div>
        <h3 className="inicio-trail__title">{t.title}</h3>
        <span className="inicio-trail__meta">{t.stageCount} etapas · {t.days} dias · {fmtHoursRound(t.totalMinutes)} de estudo</span>
        {en.stages?.length > 0 && (
          <div className="inicio-segbar inicio-segbar--trail" aria-hidden="true">
            {en.stages.map((s, i) => (
              <span key={i} className={`inicio-segbar__seg ${s.current ? 'is-current' : ''}`}>
                <span style={{ width: `${s.pct}%` }} />
              </span>
            ))}
          </div>
        )}
        <div className="inicio-trail__nums">
          <span>Dia {en.day} de {en.totalDays}{en.stageNumber ? ` · Etapa ${en.stageNumber}` : ''}</span>
          <span>{en.pct}%</span>
        </div>
      </div>
      {alert && (
        <div className={`inicio-trail__alert is-${alert.tone}`}>
          <alert.icon size={17} aria-hidden="true" />
          <span className="inicio-trail__alert-text">
            <strong>{alert.title}</strong>
            {alert.text && <span>{alert.text}</span>}
          </span>
          <button
            type="button"
            className={`inicio-btn ${alert.primary ? '' : 'inicio-btn--outline'}`}
            onClick={(e) => { e.stopPropagation(); onOpen(t, alert.path); }}
          >
            {alert.cta} <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Aviso na base do card da trilha ativa, conforme o dia. */
function trailAlert(t, today, en) {
  if (!today) return null;
  const cp = today.checkpoint;
  if (cp?.late) {
    const when = cp.inDays === -1 ? 'venceu ontem' : `venceu em ${cp.date}`;
    return {
      tone: 'danger', icon: Flag, title: `Checkpoint da Etapa ${cp.stageNumber} ${when}`,
      text: 'Faça para seguir no plano.', cta: 'Fazer checkpoint', primary: true, path: `/etapas/${cp.stageNumber}`,
    };
  }
  if (!today.studyDay) {
    return { tone: 'navy', icon: CalendarDays, title: 'Hoje é folga no seu plano', text: today.subtitle, cta: 'Ver plano', path: '/plano' };
  }
  if (today.doneToday) {
    return {
      tone: 'lime', icon: CircleCheck, title: 'Plano de hoje concluído',
      text: `Dia ${en.day} de ${en.totalDays}${cp ? ` · próximo checkpoint ${inDaysLabel(cp.inDays)}` : ''}.`, cta: 'Ver plano', path: '/plano',
    };
  }
  if (cp) {
    return {
      tone: 'lime', icon: Flag, title: `Próximo checkpoint ${inDaysLabel(cp.inDays)}`,
      text: `Etapa ${cp.stageNumber} · ${cp.date}.`, cta: 'Ver etapa', path: `/etapas/${cp.stageNumber}`,
    };
  }
  return null;
}

function CompactTrail({ trail: t, onOpen }) {
  const en = t.enrollment;
  const state = !t.hasStages
    ? { label: 'Em preparação', tone: 'amber' }
    : en ? { label: 'Pausada', tone: 'navy' } : { label: 'Não iniciada', tone: 'navy' };
  return (
    <button type="button" className="inicio-card inicio-ctrail" onClick={onOpen}>
      <span className="inicio-trail__badges">
        <TypeBadge type={t.type} />
        <span className={`inicio-pill inicio-pill--${state.tone}`}>{state.label}</span>
        {t.examCode && <code className="inicio-trail__code">{t.examCode}</code>}
      </span>
      <span className="inicio-ctrail__title">{t.title}</span>
      <span className="inicio-ctrail__foot">
        <span className="inicio-trail__meta">
          {t.hasStages
            ? `${t.stageCount} ${t.stageCount === 1 ? 'etapa' : 'etapas'} · ${t.days} ${t.days === 1 ? 'dia' : 'dias'} · ${fmtHoursRound(t.totalMinutes)} de estudo`
            : `${t.areas.length} áreas · plano ainda sem datas`}
        </span>
        <span className="inicio-ctrail__go">{t.hasStages && !en ? 'Começar' : 'Ver trilha'} <ArrowRight size={14} aria-hidden="true" /></span>
      </span>
    </button>
  );
}

/** Chips dos temas populares para quem ainda não segue nenhum: clique segue ou deixa de seguir. */
function FollowTags({ tags }) {
  const [following, setFollowing] = useState({});
  const toggle = async (t) => {
    setFollowing(f => ({ ...f, [t.slug]: !f[t.slug] }));
    try {
      const r = await tagService.toggleFollow(t.slug);
      setFollowing(f => ({ ...f, [t.slug]: r.following }));
    } catch {
      setFollowing(f => ({ ...f, [t.slug]: !f[t.slug] }));
    }
  };
  return (
    <div className="inicio-follow">
      <span>Siga temas para ver os artigos certos aqui:</span>
      {tags.map(t => (
        <button
          key={t.slug}
          type="button"
          className={`inicio-follow__chip ${following[t.slug] ? 'is-on' : ''}`}
          aria-pressed={Boolean(following[t.slug])}
          onClick={() => toggle(t)}
        >
          {following[t.slug] ? <Check size={13} aria-hidden="true" /> : <Plus size={13} aria-hidden="true" />}{t.name}
        </button>
      ))}
    </div>
  );
}

function HomeArticle({ article: a, onOpen }) {
  const tag = a.tags?.[0];
  const course = a.courses?.[0];
  return (
    <button type="button" className="inicio-card inicio-article" onClick={onOpen}>
      <span className="inicio-article__eyebrow">
        {tag && <span className="inicio-article__tag">#{tag.name}</span>}
        {course && <span>{tag ? ' · ' : ''}{course.name}</span>}
      </span>
      <span className="inicio-article__title">{a.title}</span>
      <span className="inicio-article__meta"><strong>{a.author?.name}</strong> · {a.readingMinutes} min</span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// Hero
// ═══════════════════════════════════════════════════════════════

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Citação discreta entre o topo e a saudação: troca a cada 8s (uma fixa com movimento reduzido). */
function Quotes() {
  const [i, setI] = useState(() => Math.floor(Math.random() * QUOTES.length));
  const [visible, setVisible] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reducedMotion()) return undefined;
    let fade;
    const timer = setTimeout(() => {
      setVisible(false);
      fade = setTimeout(() => { setI(n => (n + 1) % QUOTES.length); setVisible(true); }, 400);
    }, 8000);
    return () => { clearTimeout(timer); clearTimeout(fade); };
  }, [i, tick]);

  const pick = (n) => {
    if (n === i) return;
    setVisible(false);
    setTimeout(() => { setI(n); setVisible(true); setTick(t => t + 1); }, 400);
  };
  return (
    <div className="inicio-quote">
      {/* Todas as frases no mesmo lugar: a altura fica a da mais longa e a tela não pula ao trocar */}
      <div className="inicio-quote__stack" aria-live="polite">
        {QUOTES.map(([text, author], n) => (
          <p key={n} className={n === i && visible ? 'is-active' : ''} aria-hidden={n !== i}>
            “{text}” <span>— {author}</span>
          </p>
        ))}
      </div>
      {!reducedMotion() && (
        <div className="inicio-quote__dots" role="group" aria-label="Citações">
          {QUOTES.map((q, n) => (
            <button
              key={n}
              type="button"
              className={n === i ? 'is-active' : ''}
              onClick={() => pick(n)}
              aria-label={`Citação ${n + 1} de ${QUOTES.length}`}
              aria-current={n === i}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

/** "Como quer ser chamado(a)" do primeiro acesso, ou o primeiro nome. */
function firstName(user) {
  return user?.displayName || user?.name?.split(' ')[0] || '';
}

/** "Quarta, 29 de outubro" */
function dateLabel() {
  const s = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }).replace('-feira', '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function inDaysLabel(n) {
  if (n <= 0) return 'hoje';
  if (n === 1) return 'amanhã';
  return `em ${n} dias`;
}

export default CourseSelector;
